// src/modules/units/tyres/views/retread-batch.api.js
//
// The retread-batch read/write client. Reads go through call() + shapeResult();
// every write goes through mutate(), which on success emits RETREAD_BATCH_CHANGED
// and — for operations that move tyres (dispatch, receipt, cancel, rollback) —
// TYRE_EVENTS.CHANGED as well, so the register, dashboard, and any open tyre
// detail refetch.
//
// All the response shaping and the recovery decision live in the pure core
// (shared/retread-batch-core.js); this file only does fetch + event emit, the same
// thin-shell shape as tyres.api.js. See the core for why the operation "saga"
// exists and why these calls are synchronous (a 2xx is already settled — there is
// nothing to poll).

import { getUserData } from '../../../auth/views/login/auth.api.js';
import { emitTyreEvent, TYRE_EVENTS } from '../shared/tyre-events.js';
import { shapeResult, RECOVERY, RETREAD_BATCH_STATUS } from '../shared/retread-batch-core.js';

const baseUrl = 'https://dashboard.pressuretrack.co.za/api';
const enc = encodeURIComponent;

// Paged reads collect ALL rows with an explicit positive page size — never limit=0,
// never "no limit". Without a limit the list endpoint applies its own default page
// size and SILENTLY TRUNCATES once a customer has more batches than that default,
// the same trap units.api.js and suppliers.api.js document. RB_MAX_PAGES is a
// seatbelt, not a ceiling anyone should reach.
const RB_PAGE_SIZE = 100;
const RB_MAX_PAGES = 50;

function toQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

// One fetch, normalised to { ok, status, body } for the core to shape. A network
// or abort error is turned into a non-HTTP failure so callers always get an
// envelope rather than a thrown exception to catch at every call site.
async function call(path, options = {}) {
  const { token } = getUserData() || {};

  let response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
      credentials: 'include'
    });
  } catch (err) {
    const cancelled = err?.name === 'AbortError';
    return { ok: false, status: 0, body: { message: cancelled ? 'Request cancelled.' : 'Network error.' } };
  }

  const body = await response.json().catch(() => null);
  return { ok: response.ok, status: response.status, body };
}

// Write wrapper. Emits RETREAD_BATCH_CHANGED on success; also CHANGED when the
// operation moves tyres. The { method, path, body } replay descriptor is carried
// on the result so a failed op can be resumed byte-identically (see
// resumeRetreadOperation). `reason` is a debugging label on the event; no
// subscriber branches on it, so omitting it still yields a correct notification.
async function mutate(path, options, { movesTyres = false, reason } = {}) {
  const res = await call(path, options);
  const shaped = shapeResult(res, { method: options.method, path, body: options.body ?? null });

  if (shaped.success) {
    emitTyreEvent(TYRE_EVENTS.RETREAD_BATCH_CHANGED, { reason });
    if (movesTyres) emitTyreEvent(TYRE_EVENTS.CHANGED, { reason });
  }
  return shaped;
}

// ---- reads ----------------------------------------------------------------

/** List batch headers. params: { status?, supplierId?, sort?, limit?, offset? }. */
export async function fetchRetreadBatches(params = {}, { signal } = {}) {
  const res = await call(`/retread-batch${toQuery(params)}`, { method: 'GET', signal });
  return shapeResult(res, null);
}

/**
 * List EVERY batch header for the current filter, paged to completion.
 *
 * `fetchRetreadBatches` is a single page; a bare call with no `limit` leans on the
 * endpoint's default page size and silently truncates once there are more batches
 * than that default. This pages with an explicit positive `limit` until a short
 * page comes back — the same shape as fetchSuppliers / fetchAllTyres.
 *
 * Ordering is deliberately NOT decided here: `sort: 'reference'` is sent only so
 * offset paging walks a stable, total order (references are unique). The DISPLAY
 * order is applied on the client (see sortBatchesForDisplay), where it can't ride
 * on an unverified server sort-direction param. A failed page is returned as-is
 * (its error envelope) rather than a silently partial list.
 *
 * params: { status?, supplierId? }. Success envelope mirrors fetchRetreadBatches
 * ({ success, status, batches, count }), so callers read `.batches` unchanged.
 */
export async function fetchAllRetreadBatches(params = {}, { signal } = {}) {
  const rows = [];
  let count = NaN;
  let status = 200;

  for (let page = 0; page < RB_MAX_PAGES; page++) {
    const res = await fetchRetreadBatches(
      { ...params, sort: 'reference', limit: RB_PAGE_SIZE, offset: rows.length },
      { signal }
    );
    if (!res.success) return res;

    status = res.status;
    const pageRows = Array.isArray(res.batches) ? res.batches : [];
    // shapeResult gives a number or null; keep null out of the >= check below,
    // where Number(null) === 0 would stop paging after the very first full page.
    count = Number.isFinite(res.count) ? res.count : NaN;
    rows.push(...pageRows);

    if (pageRows.length < RB_PAGE_SIZE) break;             // last (short) page
    if (Number.isFinite(count) && rows.length >= count) break; // collected them all
  }

  return {
    success: true,
    status,
    batches: rows,
    count: Number.isFinite(count) ? count : rows.length,
    data: { count: Number.isFinite(count) ? count : rows.length, results: rows }
  };
}

/** One batch header plus its lines and any recovery warnings. */
export async function fetchRetreadBatch(retreadBatchId, { signal } = {}) {
  const res = await call(`/retread-batch/${enc(retreadBatchId)}`, { method: 'GET', signal });
  return shapeResult(res, null);
}

// ---- draft assembly (no tyre movement) ------------------------------------

/**
 * Create a DRAFT consignment. payload:
 *   { supplierId, expectedReturnDate?, dispatchReference?, notes?, externalTyreIds? }
 * A create has no operationId to dedupe against, so a blind re-POST could make a
 * SECOND draft — on failure we do not advertise a forward retry; the caller should
 * refetch the list and decide. (A lock 409 is left as-is: nothing was created.)
 */
export async function createRetreadBatch(payload, { reason = 'create' } = {}) {
  const shaped = await mutate('/retread-batch', { method: 'POST', body: JSON.stringify(payload) }, { movesTyres: false, reason });
  if (!shaped.success && !shaped.locked) {
    shaped.recovery = RECOVERY.NONE;
    shaped.retryable = false;
    shaped._replay = null;
  }
  return shaped;
}

/**
 * Edit a batch, or cancel it via { status: 'CANCELLED' }. Editing the supplier is
 * only allowed while DRAFT (backend-enforced). Cancelling an OPEN batch reverts its
 * tyres AT_RETREAD -> IN_STOCK, so a cancel moves tyres; a plain edit does not.
 */
export async function updateRetreadBatch(retreadBatchId, payload, { reason } = {}) {
  const cancelling = payload?.status === RETREAD_BATCH_STATUS.CANCELLED;
  return mutate(
    `/retread-batch/${enc(retreadBatchId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    { movesTyres: cancelling, reason: reason || (cancelling ? 'cancel' : 'edit') }
  );
}

/** Cancel convenience over updateRetreadBatch. */
export async function cancelRetreadBatch(retreadBatchId, { reason = 'cancel' } = {}) {
  return updateRetreadBatch(retreadBatchId, { status: RETREAD_BATCH_STATUS.CANCELLED }, { reason });
}

/** Add eligible tyres to a DRAFT. No tyre movement — they stay In Stock. */
export async function addRetreadBatchLines(retreadBatchId, externalTyreIds, { reason = 'add-lines' } = {}) {
  return mutate(
    `/retread-batch/${enc(retreadBatchId)}/lines`,
    { method: 'POST', body: JSON.stringify({ externalTyreIds }) },
    { movesTyres: false, reason }
  );
}

/** Remove one untouched line from a DRAFT (204 on success). */
export async function removeRetreadBatchLine(retreadBatchId, retreadBatchLineId, { reason = 'remove-line' } = {}) {
  return mutate(
    `/retread-batch/${enc(retreadBatchId)}/lines/${enc(retreadBatchLineId)}`,
    { method: 'DELETE' },
    { movesTyres: false, reason }
  );
}

// ---- operations (move tyres; resumable saga) ------------------------------

/** Dispatch a DRAFT. payload: { dispatchedAt?, dispatchReference? }. */
export async function dispatchRetreadBatch(retreadBatchId, payload = {}, { reason = 'dispatch' } = {}) {
  return mutate(
    `/retread-batch/${enc(retreadBatchId)}/dispatch`,
    { method: 'POST', body: JSON.stringify(payload) },
    { movesTyres: true, reason }
  );
}

/**
 * Receive one supplier delivery spanning one or more batches. payload:
 *   { supplierId, returnedAt?, deliveryNoteReference?,
 *     items: [{ externalTyreId, outcome, newTreadDepth?, newGrade?, cost?, rejectReason?, notes? }] }
 * On a partial failure the error envelope carries batches[] (see the core).
 */
export async function receiveRetreadDelivery(payload, { reason = 'receive' } = {}) {
  return mutate(
    '/retread-batch/receive',
    { method: 'POST', body: JSON.stringify(payload) },
    { movesTyres: true, reason }
  );
}

/**
 * Receive tyres against ONE batch. payload:
 *   { returnedAt?, deliveryNoteReference?, items: [{ externalTyreId, outcome, ... }] }
 */
export async function receiveAgainstRetreadBatch(retreadBatchId, payload, { reason = 'receive' } = {}) {
  return mutate(
    `/retread-batch/${enc(retreadBatchId)}/receive`,
    { method: 'POST', body: JSON.stringify(payload) },
    { movesTyres: true, reason }
  );
}

/**
 * Administrator-only: restore a failed operation's before-state. Last resort, after
 * a forward retry has not cleared it. A 403 (not an admin) and a 409 (no rollback
 * available) both resolve to ESCALATE; a 500 asks for the same rollback call again.
 */
export async function rollbackRetreadBatch(retreadBatchId, { reason = 'rollback' } = {}) {
  const path = `/retread-batch/${enc(retreadBatchId)}/rollback`;
  const res = await call(path, { method: 'POST' });
  const shaped = shapeResult(res, { method: 'POST', path, body: null });

  if (shaped.success) {
    emitTyreEvent(TYRE_EVENTS.RETREAD_BATCH_CHANGED, { reason });
    emitTyreEvent(TYRE_EVENTS.CHANGED, { reason }); // a restore can move tyres back
  } else if (shaped.status === 409) {
    shaped.recovery = RECOVERY.ESCALATE; // "no rollback available" — get help
    shaped.noRollback = true;
  }
  return shaped;
}

/**
 * Resume a failed operation by replaying its exact request (same endpoint, same
 * payload). Only call this when a previous result's recovery was RETRY. The backend
 * continues the saved operationId and does not double-apply. Dispatch, receipt,
 * cancel, and rollback can all move tyres, so a successful resume emits both events.
 */
export async function resumeRetreadOperation(previous, { reason = 'resume' } = {}) {
  const replay = previous?._replay;
  if (!replay?.method) {
    return { success: false, status: 0, message: 'Nothing to resume.', recovery: RECOVERY.NONE, data: null };
  }

  const res = await call(replay.path, { method: replay.method, body: replay.body ?? undefined });
  const shaped = shapeResult(res, replay);
  if (shaped.success) {
    emitTyreEvent(TYRE_EVENTS.RETREAD_BATCH_CHANGED, { reason });
    emitTyreEvent(TYRE_EVENTS.CHANGED, { reason });
  }
  return shaped;
}
